import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  CalendarClock,
  CreditCard,
  Edit3,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  Plus,
  ScrollText,
  Search,
  User,
  Users,
  Wallet
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { createFinanceClient, listFinanceClients, updateFinanceClient } from '../../../../../services/ClientsService';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { usePaymentConditions } from '../../../../../hooks/usePaymentConditions';
import {
  FinanceClient,
  PRESET_CLIENTS,
  STORAGE_KEYS,
  formatEuro,
  fullAddress,
  nextCode
} from './financeMasters';
import { useStoredList } from './useStoredList';
import { ClientAccount, ensureClientAccount, listClientAccounts, setClientAccount } from '../../../../../services/ClientAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { AddressFormSection, AttachmentsCard, AttachmentsFormSection } from './MasterParts';
import { PartyLedgerView } from './PartyLedgerView';
import '../../shared-modules.css';
import './FinanceMasters.css';

type ClientForm = Omit<FinanceClient, 'id' | 'balance'>;

const EMPTY_FORM: ClientForm = {
  name: '',
  contactPerson: '',
  email: '',
  cif: '',
  phone: '',
  street: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'España',
  account: '430000',
  paymentTerms: 'Neto 30 días',
  preferredPaymentMethod: 'Transferencia bancaria',
  billingLanguage: 'Español',
  notes: '',
  attachments: [],
  status: 'Activo'
};

const LANGUAGES = ['Español', 'Catalán', 'Gallego', 'Euskera', 'Inglés', 'Francés'];

interface FinanceClientsViewProps {
  privacyMode: boolean;
}

export const FinanceClientsView = ({ privacyMode }: FinanceClientsViewProps) => {
  const { notify } = useToast();

  const [clients, setClients] = useStoredList<FinanceClient>(STORAGE_KEYS.clients, PRESET_CLIENTS);
  const { conditions } = usePaymentConditions();
  const { methods } = usePaymentMethods();

  // Clientes reales del backend; si /client-list falla se mantiene la lista local
  useEffect(() => {
    let cancelled = false;
    listFinanceClients()
      .then(list => { if (!cancelled && list.length > 0) setClients(list); })
      .catch(err => console.error('Error fetching clients:', err));
    return () => { cancelled = true; };
  }, []);

  // Cuenta propia (430NNNN) de cada cliente en la sociedad activa, por id de cliente
  const [ownAccounts, setOwnAccounts] = useState<Record<string, ClientAccount>>({});
  // Plan de cuentas de la sociedad: de aquí salen las cuentas que se pueden elegir
  const [chartAccounts, setChartAccounts] = useState<ChartAccount[]>([]);
  // Lo elegido en "Cuenta contable": 'gen:<código>' genérica, 'acc:<id>' cuenta existente, 'new' crear una nueva
  const [accountChoice, setAccountChoice] = useState('');

  useEffect(() => {
    listChartOfAccounts()
      .then(setChartAccounts)
      .catch(err => console.error('Error fetching chart of accounts:', err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    listClientAccounts()
      .then(map => { if (!cancelled) setOwnAccounts(map); })
      .catch(err => console.error('Error fetching client accounts:', err));
    return () => { cancelled = true; };
  }, []);

  const [selectedClient, setSelectedClient] = useState<FinanceClient | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ClientForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Partidas del deudor, a pantalla completa
  const [itemsClient, setItemsClient] = useState<FinanceClient | null>(null);

  const setField = <K extends keyof ClientForm>(field: K, value: ClientForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setAccountChoice('');
    setSelectedClient(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (client: FinanceClient) => {
    const { id, balance, ...rest } = client;
    setSelectedClient(client);
    setAccountChoice('');
    setFormData({ ...EMPTY_FORM, ...rest, attachments: [...(client.attachments || [])] });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectClient = (client: FinanceClient) => {
    setIsFormOpen(false);
    setSelectedClient(client);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedClient(null);
  };

  const openItems = (client: FinanceClient) => {
    closePanel();
    setItemsClient(client);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedClient) && !isFormOpen);

  // Cuentas genéricas (430/431 de 6 dígitos) y cuentas propias de cliente (430 + 4 dígitos)
  const genericAccounts = chartAccounts.filter(a => /^43[01][0-9]{3}$/.test(a.code));
  const ownTypeAccounts = chartAccounts.filter(a => /^430[0-9]{4}$/.test(a.code));
  // Con el plan sin cargar, las dos genéricas de siempre (mismo tipo para que .map sea válido)
  const genericOptions: { code: string; name: string }[] = genericAccounts.length > 0
    ? genericAccounts
    : [{ code: '430000', name: 'Clientes (Euros)' }, { code: '431000', name: 'Efectos en cartera' }];
  const ownerOf = (accountId: string) => Object.keys(ownAccounts).find(cid => ownAccounts[cid].accountId === accountId);

  const currentChoice = (): string => {
    if (accountChoice) return accountChoice;
    const own = isEditing && selectedClient ? ownAccounts[selectedClient.id] : undefined;
    return own ? `acc:${own.accountId}` : `gen:${formData.account}`;
  };

  const handleAccountChoice = (value: string) => {
    setAccountChoice(value);
    if (value.startsWith('gen:')) {
      setField('account', value.slice(4));
    } else if (value.startsWith('acc:')) {
      const acc = chartAccounts.find(c => c.id === value.slice(4));
      if (acc) setField('account', acc.code);
    }
  };

  // Aplica lo elegido en "Cuenta contable" al cliente ya guardado y devuelve el código que queda en su ficha
  const applyAccountChoice = async (clientId: string, fallbackCode: string): Promise<string> => {
    const choice = currentChoice();
    const own = ownAccounts[clientId];
    if (choice === 'new') {
      const created = await ensureClientAccount(clientId);
      setOwnAccounts(prev => ({ ...prev, [clientId]: created }));
      notify('Éxito', `Cuenta ${created.code} creada y asociada al cliente.`, 'info');
      return created.code;
    }
    if (choice.startsWith('acc:')) {
      const accountId = choice.slice(4);
      if (own && own.accountId === accountId) return own.code;
      const linked = await setClientAccount(clientId, accountId);
      if (linked) setOwnAccounts(prev => ({ ...prev, [clientId]: linked }));
      return linked ? linked.code : fallbackCode;
    }
    // Una cuenta genérica: si tenía cuenta propia, se le quita la asociación (la cuenta queda libre en el plan)
    if (own) {
      await setClientAccount(clientId, '');
      setOwnAccounts(prev => { const next = { ...prev }; delete next[clientId]; return next; });
    }
    return fallbackCode;
  };

  const handleSaveClient = async () => {
    if (!formData.name.trim() || !formData.cif.trim()) {
      setFormError('La razón social y el CIF son obligatorios.');
      return;
    }
    const data: ClientForm = {
      ...formData,
      name: formData.name.trim(),
      cif: formData.cif.toUpperCase().trim()
    };
    const payload = { ...data, address: fullAddress(data) };
    const termsDays = conditions.find(c => c.name === data.paymentTerms)?.days ?? 30;

    setSaving(true);
    setFormError('');
    try {
      if (isEditing && selectedClient) {
        // La cuenta se asocia antes de guardar para que la ficha quede con su código definitivo
        const accountCode = await applyAccountChoice(selectedClient.id, data.account);
        data.account = accountCode;
        payload.account = accountCode;
        const record = await updateFinanceClient(selectedClient.id, payload, termsDays);
        // Lo que el backend no guarda se conserva en pantalla hasta recargar
        const updated: FinanceClient = { ...selectedClient, ...data, id: record.id };
        setClients(prev => prev.map(c => (c.id === updated.id ? updated : c)));
        setSelectedClient(updated);
        notify('Éxito', `Cliente "${updated.name}" modificado correctamente.`, 'info');
      } else {
        const record = await createFinanceClient(payload, termsDays);
        const created: FinanceClient = { ...data, id: record.id, balance: 0 };
        setClients(prev => [created, ...prev]);
        setSelectedClient(created);
        notify('Éxito', `Cliente "${created.name}" dado de alta.`, 'info');
        const choice = currentChoice();
        if (choice === 'new' || choice.startsWith('acc:')) {
          // El cliente ya está creado: si la cuenta falla se avisa y se puede asociar luego desde su ficha
          try {
            const accountCode = await applyAccountChoice(created.id, data.account);
            if (accountCode !== data.account) {
              await updateFinanceClient(created.id, { ...payload, account: accountCode }, termsDays);
              const withAccount: FinanceClient = { ...created, account: accountCode };
              setClients(prev => prev.map(c => (c.id === created.id ? withAccount : c)));
              setSelectedClient(withAccount);
            }
          } catch (err) {
            notify('Cuenta del cliente', `El cliente se creó, pero no se pudo asociar la cuenta: ${err instanceof Error ? err.message : 'error del servidor.'}`, 'warning');
          }
        }
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving client:', err);
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar el cliente.');
    } finally {
      setSaving(false);
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(query) ||
    c.cif.toLowerCase().includes(query) ||
    c.contactPerson.toLowerCase().includes(query)
  );

  const columns: ColumnDef<FinanceClient>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (c) => <span className="fin-master__code">{c.id}</span>
    },
    {
      id: 'name',
      header: 'RAZÓN SOCIAL / CIF',
      render: (c) => (
        <>
          <div className="fin-master__name">{c.name}</div>
          <div className="fin-master__sub">CIF: {c.cif}</div>
        </>
      )
    },
    {
      id: 'contact',
      header: 'CONTACTO',
      render: (c) => (
        <>
          <div className="fin-master__text">{c.contactPerson || '—'}</div>
          <div className="fin-master__sub fin-master__sub--lower">{c.email || '—'}</div>
        </>
      )
    },
    {
      id: 'account',
      header: 'CUENTA PGC',
      render: (c) => <span className="fin-master__chip" title={ownAccounts[c.id] ? ownAccounts[c.id].name : 'Sin cuenta propia: usa la genérica'}>{ownAccounts[c.id] ? ownAccounts[c.id].code : c.account}</span>
    },
    {
      id: 'paymentTerms',
      header: 'CONDICIONES DE PAGO',
      render: (c) => (
        <>
          <div className="fin-master__text">{c.paymentTerms}</div>
          <div className="fin-master__sub">{c.preferredPaymentMethod}</div>
        </>
      )
    },
    {
      id: 'balance',
      header: 'SALDO PENDIENTE',
      align: 'right',
      render: (c) => (
        <span className={`fin-master__amount ${c.balance > 0 ? 'pending' : ''}`}>{formatEuro(c.balance, privacyMode)}</span>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (c) => (
        <span className={`module-badge ${c.status === 'Activo' ? 'active' : 'inactive'}`}>{c.status}</span>
      )
    }
  ];

  // Mientras están abiertas sustituyen al maestro
  if (itemsClient) {
    return (
      <PartyLedgerView
        kind="deudor"
        party={itemsClient}
        account={ownAccounts[itemsClient.id]}
        privacyMode={privacyMode}
        onClose={() => setItemsClient(null)}
        createAccount={() => ensureClientAccount(itemsClient.id)}
        onAccountCreated={(account) => setOwnAccounts(prev => ({ ...prev, [itemsClient.id]: { ...account, clientId: itemsClient.id } }))}
      />
    );
  }

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR RAZÓN SOCIAL, CIF O PERSONA DE CONTACTO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nuevo_Cliente
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedClient || isFormOpen)}>
        <DataTable
          data={filteredClients}
          columns={columns}
          title={<><Users size={14} className="module-panel-title-icon" /> Maestro_De_Clientes</>}
          subtitle={`TOTAL: ${filteredClients.length}`}
          getRowId={(c) => c.id}
          onRowClick={handleSelectClient}
          selectedRowId={selectedClient?.id}
          emptyText="No se encontraron clientes."
          actionsHeader="ACCIONES"
          actionsColumn={(c) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectClient(c)),
                editAction(() => handleEditClick(c)),
                { label: 'Ver partidas', icon: ScrollText, tone: 'accent', onSelect: () => openItems(c) }
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Ficha_Cliente' : 'Alta_De_Nuevo_Cliente'}
            onClose={closePanel}
            submitLabel={saving ? 'Guardando...' : 'Guardar_Cliente'}
            submitDisabled={saving}
            onSubmit={handleSaveClient}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Identificativos" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Razón Social / Nombre Comercial *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Librería Central Madrid S.L."
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">CIF / NIF *</label>
                  <input
                    type="text"
                    required
                    value={formData.cif}
                    onChange={(e) => setField('cif', e.target.value.toUpperCase())}
                    placeholder="Ej. B81234567"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setField('status', e.target.value as FinanceClient['status'])}
                    className="sidebar-form-input"
                  >
                    <option value="Activo">ACTIVO</option>
                    <option value="Inactivo">INACTIVO</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Persona de Contacto</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setField('contactPerson', e.target.value)}
                  placeholder="Ej. Carlos Gómez"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Email de Facturación</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="facturas@cliente.es"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="+34 91 000 0000"
                    className="sidebar-form-input"
                  />
                </div>
              </div>
            </DetailSection>

            <AddressFormSection
              title="2. Dirección_Fiscal"
              address={formData}
              onChange={(field, value) => setField(field, value)}
            />

            <DetailSection title="3. Contabilidad_Y_Facturación" icon={BookOpen}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta Contable PGC *</label>
                  <select
                    value={currentChoice()}
                    onChange={(e) => handleAccountChoice(e.target.value)}
                    className="sidebar-form-input"
                  >
                    <optgroup label="Cuentas genéricas">
                      {genericOptions.map(a => (
                        <option key={a.code} value={`gen:${a.code}`}>{a.code} - {a.name}</option>
                      ))}
                      {!genericAccounts.some(a => a.code === formData.account) && !ownTypeAccounts.some(a => a.code === formData.account) && formData.account !== '430000' && formData.account !== '431000' && (
                        <option value={`gen:${formData.account}`}>{formData.account}</option>
                      )}
                    </optgroup>
                    {ownTypeAccounts.length > 0 && (
                      <optgroup label="Cuentas propias de cliente (430NNNN)">
                        {ownTypeAccounts.map(a => {
                          const owner = ownerOf(a.id);
                          const taken = Boolean(owner) && owner !== selectedClient?.id;
                          return (
                            <option key={a.id} value={`acc:${a.id}`} disabled={taken}>
                              {a.code} - {a.name}{taken ? ' (asignada a otro cliente)' : ''}
                            </option>
                          );
                        })}
                      </optgroup>
                    )}
                    <option value="new">➕ Crear cuenta nueva para este cliente (430NNNN)</option>
                  </select>
                  {currentChoice() === 'new' && (
                    <p className="fin-master__sub">
                      Al guardar se crea la siguiente subcuenta 430NNNN libre de la sociedad activa, con el nombre del cliente, y queda asociada a él.
                    </p>
                  )}
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Condiciones de Pago *</label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => setField('paymentTerms', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {conditions.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Forma de Pago Preferida</label>
                  <select
                    value={formData.preferredPaymentMethod}
                    onChange={(e) => setField('preferredPaymentMethod', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {methods.map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Idioma de Facturación</label>
                  <select
                    value={formData.billingLanguage}
                    onChange={(e) => setField('billingLanguage', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {LANGUAGES.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Notas y Observaciones</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Instrucciones de entrega, acuerdos comerciales, descuentos especiales..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <AttachmentsFormSection
              title="4. Documentos_Adjuntos"
              attachments={formData.attachments}
              onChange={(attachments) => setField('attachments', attachments)}
            />
          </SidebarForm>
        ) : selectedClient ? (
          <DetailPanel
            expandable
            title="Ficha_De_Cliente"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedClient)} />
            }
            cards={<AttachmentsCard attachments={selectedClient.attachments} />}
          >
            <DetailIdentity
              icon={Users}
              reference={selectedClient.id}
              name={selectedClient.name}
              subtitle={
                <span className={`module-badge ${selectedClient.status === 'Activo' ? 'active' : 'inactive'}`}>
                  {selectedClient.status}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={FileText} label="CIF_FACTURACIÓN">{selectedClient.cif}</DetailField>
              <DetailField icon={MapPin} label="DIRECCIÓN_FISCAL">{fullAddress(selectedClient) || 'No declarada'}</DetailField>
              <DetailField icon={User} label="PERSONA_DE_CONTACTO">{selectedClient.contactPerson || 'No declarada'}</DetailField>
              <DetailField icon={Mail} label="EMAIL_DE_FACTURACIÓN">
                <span className="detail-field-value lowercase">{selectedClient.email || 'No declarado'}</span>
              </DetailField>
              <DetailField icon={Phone} label="TELÉFONO">{selectedClient.phone || 'No declarado'}</DetailField>
              <DetailField icon={BookOpen} label="CUENTA_PGC">
                {ownAccounts[selectedClient.id]
                  ? `${ownAccounts[selectedClient.id].code} · ${ownAccounts[selectedClient.id].name}`
                  : `${selectedClient.account} (genérica, sin cuenta propia)`}
              </DetailField>
              <DetailField icon={CalendarClock} label="CONDICIONES_DE_PAGO">{selectedClient.paymentTerms}</DetailField>
              <DetailField icon={CreditCard} label="FORMA_DE_PAGO">{selectedClient.preferredPaymentMethod || 'No declarada'}</DetailField>
              <DetailField icon={Globe} label="IDIOMA_DE_FACTURACIÓN">{selectedClient.billingLanguage}</DetailField>
              <DetailField icon={Wallet} label="SALDO_PENDIENTE">{formatEuro(selectedClient.balance, privacyMode)}</DetailField>
            </DetailFieldList>

            {selectedClient.notes && <DetailNotes>{selectedClient.notes}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceClientsView;
